import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import style from "./index.module.scss"
import WrapperContent from "../../shared/WrapperContent";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "../../shared/icons/CloseIcon";
import InputCustom from "../../shared/forms/input-custom";
import UserIcon from "../../shared/icons/UserIcon";
import PhoneIcon from "../../shared/icons/PhoneIcon";
import EmailIcon from "../../shared/icons/EmailIcon";
import { Employee, User } from '../../../services';
import { requestErrorDisplay } from '../../../utils';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Loading from '../../shared/Loading/Loading';
import cn from 'classnames';
import SelectCustom from '../../shared/forms/select-custom';
import AccessDeniedModal from '../../shared/SubscriptionModal/AccessDeniedModal';

const PHONE_MASK = '+79999999999';

const WorkersCreatePage = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state);
  const [searchParams] = useSearchParams()
  const navigate = useNavigate();
  const driversUrl = "/workers"
  const backUrl = searchParams.get("redirectUrl") ? searchParams.get("redirectUrl") : driversUrl
  const [isLoading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurName] = useState("");
  const [iin, setIIN] = useState("")
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  // const [idNumber, setIdNumber] = useState(""); // idNumber seems unused, commented out
  const [emailExists, setEmailExists] = useState(false); // This state seems related to checkEmailExists but isn't used for display
  const [emailErrorText, setEmailErrorText] = useState(''); // This state seems unused
  const [selectedRole, setSelectedRole] = useState(null); // This state seems related to a commented-out SelectCustom
  const [isDisplayModal, changeDisplayModal] = useState(false)


  const IIN_LENGTH = 12


  const validateFormatData = () => {
    if (!name) {
      toast.error(t('workersCreate.validation.nameInvalid'));
      return false
    }

    if (!surname) {
      toast.error(t('workersCreate.validation.surnameInvalid'));
      return false
    }

    if (!email) {
      // Reusing translation key from driversCreate as it's the same message
      toast.error(t('driversCreate.validation.emailRequired'));
      return false
    }

    // Role validation is commented out in the original code
    // if (!role) {
    //   toast.error(t('workersCreate.validation.roleRequired'));
    //   return false
    // }

    // Phone number validation is commented out in the original code
    // if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 11) { // Basic check if uncommented
    //   toast.error(t('workersCreate.validation.phoneInvalid'));
    //   return false
    // }

    return true
  }


  const checkEmailExists = async (emailToCheck) => {
    try {
      const response = await User.checkEmail(emailToCheck);
      return !response.data.isExists; // Returns true if email does NOT exist (is valid)

    } catch (error) {
      // Reusing translation key from driversCreate
      console.error(t('driversCreate.errors.emailCheckError'), error);
      toast.error(t('driversCreate.errors.emailCheckError'));
      return false; // Assume error means we can't proceed
    }
  };

  const onSubmit = async () => {
    if (user && user.permissions?.includes("restricted_view")) {
      changeDisplayModal(true);
      return;
    }

    // setSelectedRole(selectedRole); // Role selection is commented out

    if (!validateFormatData()) {
      return false
    }

    const emailIsValid = await checkEmailExists(email);
    if (!emailIsValid) {
      // Reusing translation key from registration form
      toast.error(t('registration.form.error.emailExists'));
      return false;
    }

    setLoading(true)
    const data = {
      "companyId": user.id,
      "companyName": user.name,
      "type": user.type,
      "name": name,
      "surname": surname,
      "iin": iin,
      "email": email,
      // Ensure phone number is cleaned, handle potential empty string if validation is off
      "phoneNumber": phoneNumber ? phoneNumber.replace(/\D/g, '').substring(1) : '', // Remove non-digits and leading '7'
    };

    Employee.createEmployee(data)
      .then(res => res.data)
      .then(async createdData => { // Renamed 'data' to avoid conflict
        console.log('createdData', createdData)
        toast.success(t('workersCreate.toast.employeeAddedSuccess'));
        toast.info(t('workersCreate.toast.activationInstructionSent', { email: email }), { autoClose: 5000 }); // Use autoClose instead of delay
        navigate(backUrl)
      })
      .catch(error => {
        requestErrorDisplay(error) // Assuming this handles its own translations or uses generic ones
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <WrapperContent>
      {isDisplayModal && <AccessDeniedModal onClose={() => changeDisplayModal(false)} />}
      <div className={style.wrapper}>
        {/* Reusing translation key from workers page */}
        <h1 className={style.title}>{t('workers.addEmployeeButton')}</h1>
        <div className={style.content}>
          <div className={style.field}>
             {/* Reusing translation key from driversCreate */}
            <span className={style.field_label}>{t('driversCreate.label.name')}</span>
            <InputCustom
                placeholder={t('workersCreate.placeholder.enterName')}
                defaultValue={name}
                onChangeValue={setName}
                prefixStart={<UserIcon/>}
            />
          </div>
          <div className={style.field}>
             {/* Reusing translation key from driversCreate */}
            <span className={style.field_label}>{t('driversCreate.label.surname')}</span>
            <InputCustom
                placeholder={t('workersCreate.placeholder.enterSurname')}
                defaultValue={surname}
                onChangeValue={setSurName}
                prefixStart={<UserIcon/>}
            />
          </div>
          {/* Role selection is commented out in the original code
          <div className={style.field}>
            <span className={style.field_label}>{t('workersItemCard.header.role')}</span>
            <SelectCustom
              key={'title'}
              placeholder={t('workersCreate.placeholder.role')}
              keyId="value"
              keyName="title"
              // options={roles} // 'roles' variable is not defined
              defaultValue={selectedRole}
              returnObject
              onSubmit={(item) => {
                console.log("item", item)
                setRole(item.value)
                setSelectedRole(item);
              }}
            />
          </div>
          */}
          <div className={style.field}>
             {/* Reusing translation key from driversCreate */}
            <span className={style.field_label}>{t('driversCreate.label.iinNumber')}</span>
            <InputCustom
                placeholder={t('workersCreate.placeholder.enterIIN')}
                defaultValue={iin}
                onChangeValue={setIIN}
                prefixStart={<UserIcon/>}
                maxLength={IIN_LENGTH}
            />
          </div>
          <div className={style.field}>
             {/* Reusing translation key from workersItemCard */}
            <span className={style.field_label}>{t('workersItemCard.table.header.email')}</span>
            <InputCustom
                defaultValue={email}
                onChangeValue={setEmail}
                 // Reusing translation key from login
                placeholder={t('login.emailPlaceholder')}
                prefixStart={<EmailIcon/>}
            />
            {/* Displaying error message based on emailExists state seems missing in original logic */}
            {/* {emailExists && <p className={style.error_message}>{emailErrorText}</p>} */}
          </div>
          <div className={style.field}>
            <span className={style.field_label}>
               {/* Reusing translation key from driversCreate */}
              {t('driversCreate.label.phoneNumber')}
               {/* Reusing translation key from driversCreate */}
              <span className={style.optional}>{t('driversCreate.label.optional')}</span>
            </span>
            <InputMask
                value={phoneNumber}
                maskChar={null}
                mask={PHONE_MASK}
                onChange={(event) => setPhoneNumber(event.target.value)}
                disabled={false}
            >
              {(inputProps) => (
                  <InputCustom
                      {...inputProps}
                      placeholder={PHONE_MASK} // Mask itself acts as placeholder
                      prefixStart={<PhoneIcon/>}
                      // defaultValue={phoneNumber} // defaultValue is handled by InputMask value
                      // onChangeValue={setPhoneNumber} // onChangeValue is handled by InputMask onChange
                  />
              )}
            </InputMask>
          </div>
          {/* Status selection is commented out in the original code
          <div className={style.field}>
            <span className={style.field_label}>{t('workersItemCard.header.status')}</span>
            <SelectCustom
              key={'countries-from'}
              placeholder={t('workersItemCard.header.status')}
              options={[]} // Options are empty
              defaultValue={null}
              returnObject
              onSubmit={(item) => {
                console.log("item", item)
              }}
            />
          </div>
          */}

          <Link to={backUrl} className={style.buttonClose}>
            <CloseIcon/>
          </Link>
        </div>
        <div className={style.block}>
          <Link to={backUrl} className={style.buttonCancel}>
             {/* Reusing translation key from driversCreate */}
            {t('driversCreate.button.cancel')}
          </Link>

          <button
              className={cn(style.button_upload, {
                [style.button_upload_loading]: isLoading,
              })}
              onClick={onSubmit} // Removed () => as it's not needed
          >
             {/* Reusing translation key from login */}
            {!isLoading && <span>{t('login.createAccountButton')}</span>}
            {isLoading && <Loading inBlock marginBottom={0} />}
          </button>
        </div>
      </div>
    </WrapperContent>
  );
};

export default WorkersCreatePage;
